import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { closeModal, FloatModal, openModal } from '@/lib/components/Modal/Modal';
import { DeleteIconSolid, SuspendContactIcon } from '@/lib/icon';
import { Button, Modal, Spinner, toast } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { DeleteModal, SuspendContactModal } from '@/src/ContactsModule/components/ContactsModals';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';

const ContactsProfileButtons = ({ emailId }: { emailId: number }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const service = useService();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (confirmDelete) {
      // eslint-disable-next-line promise/valid-params
      deleteContact().then().catch(console.error);
      setConfirmDelete(false);
    }
  }, [confirmDelete]);

  useEffect(() => {
    if (confirmSuspend) {
      // eslint-disable-next-line promise/valid-params
      suspendContact().then().catch(console.error);
      setConfirmSuspend(false);
    }
  }, [confirmSuspend]);

  const deleteContact = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.deleteSelectedContacts({ contactsIds: [emailId] });
    closeModal(); // spinner
    if (result) {
      navigate && navigate('/v2/contacts');
    }
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };

  const suspendContact = async () => {
    openModal({
      element: (
        <FloatModal>
          <Spinner />
        </FloatModal>
      ),
    });
    const result = await service?.suspendSelectedContacts({ contactsIds: [emailId] });
    closeModal(); // spinner
    toast({
      variant: result ? 'success' : 'error',
      title: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkTitle')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertTitle'),
      body: result
        ? t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.OkBody')
        : t('CONTACTS_ACTIONS_DROPDOWN.NOTIFICATION.AlertBody'),
    });
  };

  return (
    <>
      <Flex withGap alignment="end">
        <Button
          outline
          iconLeft={<DeleteIconSolid />}
          onClick={() =>
            openModal({
              element: (
                <DeleteModal
                  description={t('CONTACT_DELETE_MODAL.description')}
                  setConfirmDelete={setConfirmDelete}
                  title={t('CONTACT_DELETE_MODAL.title')}
                />
              ),
            })
          }
        >
          {t('delete')}
        </Button>
        <Button outline iconLeft={<SuspendContactIcon />} onClick={() => setShowModal(true)}>
          {t('suspend')}
        </Button>
      </Flex>
      <Modal open={showModal} onClose={setShowModal}>
        <SuspendContactModal
          onClick={() => {
            setConfirmSuspend(true);
            setShowModal(false);
          }}
        />
      </Modal>
    </>
  );
};

export default memo(ContactsProfileButtons);
